import { useTaskItem } from 'components/task-item/useTaskItem';

import { acoesDaMinhaRealizacao } from './acoes-da-minha-realizacao';

/** Modelo do cartão: `useTaskItem` e `useTaskItemMad` devolvem os mesmos nomes. */
export type ModeloDoCartao = ReturnType<typeof useTaskItem>;

export type IdDaAcaoDaFila =
  | 'validar'
  | 'devolver'
  | 'valeu'
  | 'mandou'
  | 'uau'
  | 'finalizar-premiacao'
  | 'bonificar'
  | 'finalizar-bonificacao'
  | 'promover'
  | 'selo'
  | 'prosseguir'
  | 'encerrar'
  | 'finalizar-promocao'
  | 'autorizar-participantes'
  | 'nao-autorizar-participantes';

export interface AcaoDaFila {
  id: IdDaAcaoDaFila;
  rotulo: string;
  /** Texto do balão (`Tippy`) que o cartão antigo mostrava sobre o botão. */
  dica?: string;
  /** Ação que avança a realização (fundo azul no design); as demais são contornadas. */
  principal: boolean;
  executar: () => void;
}

export type TipoDaCaixa = 'nenhuma' | 'participantes-rejeitados' | 'apreciacao' | 'autorizacao';

export interface CaixaDaFila {
  tipo: TipoDaCaixa;
  /** Decisões já tomadas, sem ação (os botões `cursor-default` do cartão antigo). */
  estados: string[];
  acoes: AcaoDaFila[];
}

/**
 * Caixa de apreciação do cartão da fila (RDN-43). Transcreve as condições do JSX do `TaskItem`/`TaskItemMad`
 * (`index.tsx`, bloco depois de "Ver mais informações") na mesma ordem e com as mesmas expressões; só os rótulos
 * mudam para os do design. Qualquer divergência aqui aparece no teste de paridade com o cartão antigo.
 */
export function caixaDaFila(modelo: ModeloDoCartao): CaixaDaFila {
  const {
    data,
    status,
    promote,
    double,
    award,
    managerType,
    pitStopCanEvaluate,
    isView,
    showModal,
    doubleByPassCod5,
    onDouble,
    canApproveParticipants,
    canApproveParticipantsInstExpired,
  } = modelo;
  const podeApreciar = modelo.canViewBoxActions() || pitStopCanEvaluate;
  const modal = (id: string, params?: Record<string, unknown>) => () => showModal(`${id}-${data._id}`, params);

  if (podeApreciar && !isView && data?.participants_approval?.status === 'rejected') {
    return { tipo: 'participantes-rejeitados', estados: [], acoes: [] };
  }

  if (podeApreciar && !isView) {
    const estados: string[] = [];
    const acoes: AcaoDaFila[] = [];

    if (!promote && !double && status === 'aprovado') estados.push('Validada');
    if (status === 'rejeitado') estados.push('Devolvida');

    if (!data.stageNumber && (!status || status === 'aguardando')) {
      acoes.push({ id: 'validar', rotulo: 'Validar', principal: true, executar: modal('validation-accept') });
      acoes.push({ id: 'devolver', rotulo: 'Devolver', principal: false, executar: modal('validation-reject') });
    }

    if (managerType !== 'equipe') {
      if (award?.uau || award?.mandou || award?.valeu) {
        if (award?.valeu) {
          acoes.push({
            id: 'valeu',
            rotulo: 'Valeu',
            dica: 'Podem receber "Bonificação Valeu" 100% das entregas neste nível',
            principal: true,
            executar: modal('validation-award', { type: 'valeu' }),
          });
        }
        if (award?.mandou) {
          acoes.push({
            id: 'mandou',
            rotulo: 'Mandou Bem',
            dica: 'Podem receber "Bonificação Mandou Bem" até 50% das entregas neste nível (com arredomento para cima)',
            principal: false,
            executar: modal('validation-award', { type: 'mandou' }),
          });
        }
        if (award?.uau) {
          acoes.push({
            id: 'uau',
            rotulo: 'UAU',
            dica: 'Podem receber "Bonificação UAU" até 25% das entregas neste nível (com arredomento para cima)',
            principal: false,
            executar: modal('validation-award', { type: 'uau' }),
          });
        }
        acoes.push({
          id: 'finalizar-premiacao',
          rotulo: 'Finalizar',
          principal: false,
          executar: modal('validation-stop', { type: 'promote' }),
        });
      }

      if (promote || double || pitStopCanEvaluate) {
        if (double) {
          acoes.push({
            id: 'bonificar',
            rotulo: 'Bonificar',
            principal: true,
            executar: () =>
              !!doubleByPassCod5 && typeof onDouble === 'function' ? onDouble(data) : showModal(`validation-double-${data._id}`),
          });
          acoes.push({
            id: 'finalizar-bonificacao',
            rotulo: 'Finalizar',
            principal: false,
            executar: modal('validation-stop', { type: 'double' }),
          });
        }

        if (promote || pitStopCanEvaluate) {
          const diretoria = !!managerType && managerType === 'diretoria';
          acoes.push({
            id: diretoria ? 'selo' : pitStopCanEvaluate ? 'prosseguir' : 'promover',
            rotulo: diretoria ? 'Selo' : pitStopCanEvaluate ? 'Prosseguir' : 'Promover',
            dica: pitStopCanEvaluate
              ? 'Você deve avaliar se essa entrega irá prosseguir para o último nível de apreciação (Diretor/Gerente Geral UE)'
              : diretoria
                ? 'Podem receber Selos Realização Destaque até 10% das entregas bonificadas neste nível (com arredomento para cima)'
                : `Podem ser promovidas até ${data.stageNumber >= 2 ? '20%' : '10%'} das entregas bonificadas neste nível (com arredomento para cima)`,
            principal: !double,
            executar: diretoria ? modal('validation-selo') : modal('validation-promote'),
          });

          acoes.push(
            pitStopCanEvaluate
              ? {
                  id: 'encerrar',
                  rotulo: 'Encerrar',
                  dica: 'Você deve avaliar se essa entrega será concluída neste nível e não será enviada para o último nível de apreciação (Diretor/Gerente Geral UE).',
                  principal: false,
                  executar: modal('validation-stop', { type: 'pitstop' }),
                }
              : {
                  id: 'finalizar-promocao',
                  rotulo: 'Finalizar',
                  principal: false,
                  executar: modal('validation-stop', { type: 'promote' }),
                },
          );
        }
      }
    }

    return { tipo: 'apreciacao', estados, acoes };
  }

  if (modelo.canViewBoxActions() && !!canApproveParticipants) {
    const estados: string[] = [];
    const acoes: AcaoDaFila[] = [];
    const situacao = data?.participants_approval?.status;

    if (situacao === 'approved') estados.push('Participantes autorizados');
    if (situacao === 'rejected') estados.push('Participantes não autorizados');
    if (situacao === 'pending' && canApproveParticipantsInstExpired) {
      acoes.push({
        id: 'autorizar-participantes',
        rotulo: 'Autorizar',
        principal: true,
        executar: modal('validation-participants-accept'),
      });
      acoes.push({
        id: 'nao-autorizar-participantes',
        rotulo: 'Não autorizar',
        principal: false,
        executar: modal('validation-participants-reject'),
      });
    }

    return { tipo: 'autorizacao', estados, acoes };
  }

  return { tipo: 'nenhuma', estados: [], acoes: [] };
}

/** Na fila não há Excluir: quem exclui é só o cadastrante, na página dele (RDD-09, regra `canDeleteTask`). */
export interface AcoesGeraisDaFila {
  editar: boolean;
  atualizarIndicador: boolean;
  solicitarAutorizacao: boolean;
  desfazer: boolean;
  /** `enviar`: botão "Enviar feedback"; `enviado`: estado "Feedback enviado"; `null`: nenhum dos dois. */
  feedback: 'enviar' | 'enviado' | null;
  /** Aviso de quantidade de participantes pendente sobre a caixa de apreciação. */
  avisoDeParticipantesPendentes: boolean;
}

/** Ações fora da caixa de apreciação, com as mesmas expressões do JSX do cartão antigo (RDN-43). */
export function acoesGeraisDaFila(modelo: ModeloDoCartao): AcoesGeraisDaFila {
  const { reset, editable, isView, canViewResetButton, hasFeedback, data, pitStopCanEvaluate } = modelo;

  const { excluir, ...daMinhaRealizacao } = acoesDaMinhaRealizacao(modelo);

  return {
    ...daMinhaRealizacao,
    desfazer: (!!editable || !!reset) && !isView && !!canViewResetButton,
    feedback: modelo.canSendFeedback() && typeof hasFeedback === 'boolean' ? (hasFeedback ? 'enviado' : 'enviar') : null,
    avisoDeParticipantesPendentes:
      (modelo.canViewBoxActions() || pitStopCanEvaluate) && !isView && data?.participants_approval?.status === 'pending',
  };
}
